// Save/load offline photo bytes via Capacitor Filesystem for later attachment upload.
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";

const PHOTO_DIR = "lion_offline_photos";

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result || "";
      const base64 = String(result).split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const base64ToBlob = (base64, type) => {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: type || "application/octet-stream" });
};

export const savePhotoLocally = async (file) => {
  const id = uuidv4();
  const safeName = (file.name || "photo.jpg").replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${PHOTO_DIR}/${id}_${safeName}`;

  if (Capacitor.isNativePlatform()) {
    const base64 = await blobToBase64(file);
    await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Data,
      recursive: true,
    });
    return {
      localPath: path,
      name: file.name,
      type: file.type,
      size: file.size,
    };
  }

  // Browser / non-native: IndexedDB via localforage would be heavier — keep base64 in Filesystem polyfill fallback
  const base64 = await blobToBase64(file);
  try {
    await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Data,
      recursive: true,
    });
  } catch (_e) {
    // ponytail: web fallback — stash on window memory map if Filesystem unavailable
    if (!window.__lionOfflinePhotos) window.__lionOfflinePhotos = {};
    window.__lionOfflinePhotos[path] = { base64, type: file.type, name: file.name, size: file.size };
  }
  return {
    localPath: path,
    name: file.name,
    type: file.type,
    size: file.size,
  };
};

export const readPhotoAsFile = async ({ localPath, name, type }) => {
  if (window.__lionOfflinePhotos?.[localPath]) {
    const stored = window.__lionOfflinePhotos[localPath];
    const blob = base64ToBlob(stored.base64, stored.type || type);
    return new File([blob], name || stored.name || "photo.jpg", { type: stored.type || type });
  }

  const result = await Filesystem.readFile({
    path: localPath,
    directory: Directory.Data,
  });
  const data = result.data;
  // Capacitor may return base64 string or Blob depending on platform/version
  let blob;
  if (data instanceof Blob) {
    blob = data;
  } else {
    blob = base64ToBlob(String(data), type);
  }
  return new File([blob], name || "photo.jpg", { type: type || blob.type || "image/jpeg" });
};

export const clearAllLocalPhotos = async () => {
  window.__lionOfflinePhotos = {};
  try {
    await Filesystem.rmdir({ path: PHOTO_DIR, directory: Directory.Data, recursive: true });
  } catch (_e) {
    // dir missing
  }
};

export const deletePhotoLocally = async (localPath) => {
  if (!localPath) return;
  if (window.__lionOfflinePhotos?.[localPath]) {
    delete window.__lionOfflinePhotos[localPath];
    return;
  }
  try {
    await Filesystem.deleteFile({ path: localPath, directory: Directory.Data });
  } catch (_e) {
    // already gone
  }
};

// Collect pending photo refs from a JSON-serializable body (deep walk)
export const collectPhotoRefs = (body) => {
  const refs = [];
  const walk = (node, path) => {
    if (!node || typeof node !== "object") return;
    if (node.localPath && node.pendingUpload) {
      refs.push({
        path,
        localPath: node.localPath,
        name: node.name,
        type: node.type,
        fileID: node.fileID,
      });
      return;
    }
    if (_.isArray(node)) {
      _.each(node, (child, i) => walk(child, [...path, i]));
    } else {
      _.each(node, (child, key) => {
        if (key === "attachment" || child instanceof File || child instanceof Blob) return;
        walk(child, [...path, key]);
      });
    }
  };
  walk(body, []);
  return refs;
};

export const applyBlobSignedIds = (body, uploads) => {
  const clone = _.cloneDeep(body);
  _.each(uploads, (upload) => {
    let cursor = clone;
    const path = upload.path || [];
    for (let i = 0; i < path.length - 1; i++) {
      cursor = cursor[path[i]];
    }
    const last = path[path.length - 1];
    if (cursor && last != null && cursor[last]) {
      cursor[last] = {
        ..._.omit(cursor[last], ["localPath", "pendingUpload", "uploading", "attachment", "url"]),
        blob_signed_id: upload.blob_signed_id,
        uploaded: true,
      };
    }
  });
  return clone;
};
